import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import { connectorsByIdentifier } from "../../shared/connectors";
import {
  supportsGitAuth,
  supportsManualGitWebhook,
} from "../../shared/connectors/gitSyncCapabilities";
import { matchesUrlPattern } from "../../shared/connectors/matchesUrlPattern";
import {
  domainToRegex,
  widenUrlPattern,
} from "../../shared/connectors/urlPatternConversion";
import { sharedMutators } from "../../shared/mutators";
import { withConnectorDestinationProof } from "../sealSecret";
import { GIT_SYNC_STALE_MS } from "../../shared/mutators/syncGit";
import {
  GIT_SYNC_PAUSED_MESSAGE,
  GIT_SYNC_ROOT,
} from "../../shared/gitSyncRemote";
import { personalUseBlocked } from "../../shared/personalUse";
import { queries } from "../../shared/queries";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { useConnectorCreator } from "../hooks/useConnectorCreator";
import { useDeadlinePassed } from "../hooks/useDeadlinePassed";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useHasAccess } from "../hooks/useHasAccess";
import { useFormatRelativeTime } from "../hooks/useFormatRelativeTime";
import { useMakeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorSelect } from "./ConnectorSelect";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { Link } from "./Link";
import { SettingsCard as Card } from "./SettingsCard";
import { Text } from "./Text";
import { Toggle } from "./Toggle";

const Header = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 8px;
`;

const HeaderText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RemoteBox = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const RemoteIcon = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  color: var(--text-primary);
`;

const RemoteInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const RepoLink = styled("a")`
  display: block;
  min-width: 0;
  align-self: flex-start;
  max-width: 100%;
  color: var(--text-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 8px;
`;

function parseRepoUrl(raw: string): URL | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" && url.hostname ? url : null;
  } catch {
    return null;
  }
}

function isGitlabHost(host: string): boolean {
  return host === "gitlab.com" || host.endsWith(".gitlab.com");
}

// A connector on the repository's domain whose pattern doesn't (yet) allow the
// repository URL — e.g. a Bitbucket connector scoped to api.bitbucket.org
// while repos live on bitbucket.org. Surfaced so connecting can widen the
// pattern with consent instead of the connector silently not appearing.
function coversSameDomain(pattern: string, host: string): boolean {
  const domain = host.split(".").slice(-2).join(".");
  return pattern.replace(/\\/g, "").includes(domain);
}

type ConnectSuggestion = {
  identifier: string;
  label: string;
  subtitle?: string;
  overrides?: {
    name?: string;
    configuration?: Record<string, string>;
    urlPattern?: string;
  };
};

// GitHub and GitLab are the first-class suggestions — they bring webhooks and
// PR tracking. The escape hatch for everything else is a generic API key
// connector prefilled for basic auth, which any git server speaking smart
// HTTP accepts. An unknown host can be self-hosted GitLab or GitHub
// Enterprise just as well as a bare git server, so all options are offered.
// New connectors are scoped to the repository's host so their credential is
// only ever injected toward that server.
function connectSuggestions(repoUrl: URL): ConnectSuggestion[] {
  const host = repoUrl.hostname;
  const hostPattern = domainToRegex(repoUrl.origin);
  const firstClass =
    host === "github.com"
      ? ["githubApp"]
      : isGitlabHost(host)
        ? ["gitlab"]
        : ["githubApp", "gitlab"];
  return [
    ...firstClass.map((identifier) => {
      const definition = connectorsByIdentifier[identifier]!;
      return {
        identifier,
        label: definition.name,
        subtitle: definition.authLabel,
        overrides:
          identifier === "gitlab" && !isGitlabHost(host)
            ? {
                configuration: { instanceUrl: repoUrl.origin },
                urlPattern: hostPattern,
              }
            : undefined,
      };
    }),
    {
      identifier: "apiKey",
      label: "Git server",
      subtitle: "HTTP basic auth",
      overrides: {
        name: host,
        configuration: { authMethod: "basic" },
        urlPattern: hostPattern,
      },
    },
  ];
}

function repoNameFromUrl(remoteUrl: string): string {
  const path = new URL(remoteUrl).pathname
    .replace(/^\/+/, "")
    .replace(/\.git$/, "");
  return path || remoteUrl;
}

type Props = {
  spaceId: string;
  canManageSpace: boolean;
};

export function GitSyncForm({ spaceId, canManageSpace }: Props) {
  const makeUrl = useMakeUrl();
  const z = useZero();
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const canConfigure = canManageSpace && canPushLive;
  const canConfigureRef = useRef(canConfigure);
  useLayoutEffect(() => {
    canConfigureRef.current = canConfigure;
  }, [canConfigure]);
  const formatRelativeTime = useFormatRelativeTime();
  const [space, spaceResult] = useQuery(queries.navSpace({ spaceId }));
  const [gitSync, gitSyncResult] = useQuery(
    queries.loadSpaceGitRemote({ spaceId })
  );
  const [allApps, appsResult] = useQuery(queries.connectorsIndex());
  const [spaceGrants, grantsResult] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const { userGroupIds: userGroupMemberships, loaded: membershipsLoaded } =
    useGroupMemberships();
  const availabilityReady =
    membershipsLoaded &&
    [spaceResult, gitSyncResult, appsResult, grantsResult].every(
      (result) => result.type === "complete"
    );
  const remoteReady = gitSyncResult.type === "complete";
  const [formOpen, setFormOpen] = useState(false);
  const [repoUrlValue, setRepoUrlValue] = useState("");
  const [directoryValue, setDirectoryValue] = useState("");
  const [selectedAppId, setSelectedAppId] = useState("");
  const [configureError, setConfigureError] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const configuringRef = useRef(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState<
    false | "toggle" | "row"
  >(false);
  const createConnector = useConnectorCreator(setSelectedAppId, spaceId);

  // syncStartedAt is set by the syncGit mutation and cleared when the
  // server-side task settles, so every client sees the same in-progress state.
  const syncStartedAt = gitSync?.syncStartedAt ?? null;
  const syncStale = useDeadlinePassed(
    syncStartedAt != null ? syncStartedAt + GIT_SYNC_STALE_MS : null
  );
  const isSyncing = syncStartedAt != null && !syncStale;

  const hasRemote = Boolean(gitSync?.remoteUrl);
  const connectorMissing = hasRemote && !gitSync?.appUsage?.connectedAppId;
  const remoteApp = gitSync?.appUsage?.connectedApp;
  const gitApps =
    !space || spaceGrants.length === 0
      ? []
      : allApps.filter(
          (app) =>
            supportsGitAuth(app.identifier) &&
            !personalUseBlocked(app, { kind: "space", mode: space.mode }) &&
            isSupplierAvailableFromGrants(
              "space_uses_connected_apps",
              spaceGrants,
              app.roleGrantsAsScope,
              userGroupMemberships
            )
        );

  const repoUrl = parseRepoUrl(repoUrlValue);
  const matchingApps = repoUrl
    ? gitApps.filter((app) => matchesUrlPattern(app.urlPattern, repoUrl.href))
    : [];
  const nearMissApps = repoUrl
    ? gitApps.filter(
        (app) =>
          !matchesUrlPattern(app.urlPattern, repoUrl.href) &&
          coversSameDomain(app.urlPattern, repoUrl.hostname)
      )
    : [];
  const selectableApps = [...matchingApps, ...nearMissApps];
  const suggestions = repoUrl ? connectSuggestions(repoUrl) : [];
  const selectedApp = selectableApps.find((app) => app.id === selectedAppId);
  const needsWiden =
    repoUrl != null &&
    selectedApp != null &&
    !matchesUrlPattern(selectedApp.urlPattern, repoUrl.href);
  const canWiden = useHasAccess("connected_app:write", selectedApp?.id);
  const canWidenRef = useRef(canWiden);
  useLayoutEffect(() => {
    canWidenRef.current = canWiden;
  }, [canWiden]);

  const handleConfigure = async () => {
    if (
      !availabilityReady ||
      !canConfigure ||
      !repoUrl ||
      !selectedApp ||
      configuringRef.current
    )
      return;
    configuringRef.current = true;
    setConfiguring(true);
    setConfigureError(false);
    const saved = await (async () => {
      if (needsWiden) {
        if (!canWiden) return false;
        const update = await withConnectorDestinationProof(
          {
            ...selectedApp,
            configuration: selectedApp.configuration ?? {},
          },
          {
            urlPattern: widenUrlPattern(
              selectedApp.urlPattern,
              domainToRegex(repoUrl.origin)
            ),
          }
        );
        if (!canConfigureRef.current || !canWidenRef.current) return false;
        const widened = await z.mutate(
          sharedMutators.updateConnectedApp(update)
        ).server;
        if (widened.type !== "success") return false;
      }
      if (!canConfigureRef.current) return false;
      const configured = await z.mutate(
        sharedMutators.configureGitSync({
          spaceId,
          remoteUrl: repoUrlValue.trim(),
          connectedAppId: selectedApp.id,
          directory: directoryValue.trim() || undefined,
        })
      ).server;
      return configured.type === "success";
    })().catch(() => false);
    configuringRef.current = false;
    setConfiguring(false);
    if (saved) {
      setRepoUrlValue("");
      setDirectoryValue("");
      setSelectedAppId("");
      setFormOpen(false);
    } else {
      setConfigureError(true);
    }
  };

  const handleDisconnect = async () => {
    if (!remoteReady) return;
    const reopenForm = confirmDisconnect === "row";
    await z.mutate(sharedMutators.disconnectGitSync({ spaceId })).client;
    setConfirmDisconnect(false);
    setFormOpen(reopenForm && canConfigure);
  };

  const handleSync = async () => {
    if (!remoteReady) return;
    await z.mutate(sharedMutators.syncGit({ spaceId, action: "sync" })).client;
  };

  return (
    <>
      <Card>
        <Header>
          <HeaderText>
            <Text variant="text-regular-md">Git sync</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Sync workflows to a git repository
            </Text>
          </HeaderText>
          {canManageSpace && (hasRemote || canConfigure) && (
            <Toggle
              ariaLabel="Git sync"
              disabled={!remoteReady || configuring}
              isOn={hasRemote || formOpen}
              onToggle={() => {
                if (hasRemote) {
                  setConfirmDisconnect("toggle");
                } else if (formOpen) {
                  setFormOpen(false);
                  setRepoUrlValue("");
                  setDirectoryValue("");
                  setSelectedAppId("");
                } else {
                  setFormOpen(true);
                }
              }}
            />
          )}
        </Header>

        {hasRemote && (
          <>
            <RemoteBox>
              <RemoteIcon>
                {remoteApp ? (
                  <ConnectorLogo
                    template={{
                      name: remoteApp.name,
                      identifier: remoteApp.identifier,
                    }}
                    size={16}
                  />
                ) : (
                  <Icon name="git-branch" size={16} />
                )}
              </RemoteIcon>
              <RemoteInfo>
                <RepoLink
                  href={gitSync!.remoteUrl.replace(/\.git$/, "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={gitSync!.remoteUrl}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {repoNameFromUrl(gitSync!.remoteUrl)}
                    {gitSync!.directory !== "/" && ` › ${gitSync!.directory}`}
                  </Text>
                </RepoLink>
                <Text variant="text-small" color="var(--text-secondary)">
                  {remoteApp && (
                    <>
                      {"Via "}
                      <Link
                        to={makeUrl(routes.modal.connector, {
                          id: remoteApp.id,
                        })}
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {remoteApp.name}
                      </Link>
                      {" · "}
                    </>
                  )}
                  {isSyncing
                    ? "Syncing…"
                    : gitSync!.lastSyncedAt
                      ? `Last synced ${formatRelativeTime(gitSync!.lastSyncedAt)}`
                      : "Not synced yet"}
                </Text>
              </RemoteInfo>
              {canManageSpace && (
                <>
                  <Button
                    variant="secondary"
                    icon="refresh-cw"
                    iconSpinning={isSyncing}
                    onClick={handleSync}
                    disabled={!remoteReady || isSyncing || connectorMissing}
                    aria-label="Sync"
                    title="Sync"
                  />
                  <Button
                    variant="secondary"
                    icon="circle-minus"
                    onClick={() => setConfirmDisconnect("row")}
                    disabled={!remoteReady}
                    aria-label="Disconnect"
                    title="Disconnect"
                  />
                </>
              )}
            </RemoteBox>
            {connectorMissing && (
              <Field>
                <Text variant="text-small" color="var(--red-600)">
                  {GIT_SYNC_PAUSED_MESSAGE}
                </Text>
                {canConfigure && !formOpen && (
                  <div>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setRepoUrlValue(gitSync!.remoteUrl);
                        setDirectoryValue(
                          gitSync!.directory === GIT_SYNC_ROOT
                            ? ""
                            : gitSync!.directory
                        );
                        setFormOpen(true);
                      }}
                    >
                      Choose a connector
                    </Button>
                  </div>
                )}
              </Field>
            )}
            {!isSyncing && !connectorMissing && gitSync!.lastError && (
              <Text
                variant="text-small"
                color="var(--red-600)"
                style={{ padding: "0 8px" }}
              >
                {gitSync!.lastError}
              </Text>
            )}
            {!isSyncing &&
              !gitSync!.lastError &&
              gitSync!.mainPushDeclinedAt != null && (
                <Text
                  variant="text-small"
                  color="var(--text-secondary)"
                  style={{ padding: "0 8px" }}
                >
                  This repository only accepts pull requests to main, so changes
                  to live workflows reach the remote with the next merged pull
                  request.
                </Text>
              )}
            {canConfigure &&
              remoteApp &&
              supportsManualGitWebhook(remoteApp.identifier) && (
                <WebhookStatus
                  spaceId={spaceId}
                  connectorId={remoteApp.id}
                  gitSync={gitSync!}
                  disabled={!availabilityReady}
                />
              )}
          </>
        )}
        {(!hasRemote || connectorMissing) && formOpen && canConfigure && (
          <>
            <Field>
              <Text variant="text-small-md">Repository URL</Text>
              <Input
                placeholder="https://github.com/org/repo.git or https://gitlab.com/group/repo.git"
                value={repoUrlValue}
                onChange={(e) => setRepoUrlValue(e.target.value)}
                disabled={configuring}
                style={{ width: "100%" }}
              />
              {repoUrlValue.trim() && !repoUrl && (
                <Text variant="text-small" color="var(--text-secondary)">
                  Enter an https:// repository URL.
                </Text>
              )}
            </Field>
            {repoUrl && (
              <Field>
                <Text variant="text-small-md">Directory</Text>
                <Input
                  placeholder="Repository root"
                  value={directoryValue}
                  onChange={(e) => setDirectoryValue(e.target.value)}
                  disabled={configuring}
                  style={{ width: "100%" }}
                />
                <Text variant="text-small" color="var(--text-secondary)">
                  Optional. Sync this space with a directory instead of the
                  repository root; everything outside it — other spaces, or an
                  existing monorepo’s code — is left untouched.
                </Text>
              </Field>
            )}
            {repoUrl && (
              <Field>
                <Text variant="text-small-md">Connector</Text>
                <ConnectorSelect
                  disabled={!availabilityReady || configuring}
                  apps={selectableApps}
                  value={selectedApp?.id ?? null}
                  selectedName={selectedApp?.name ?? null}
                  onChange={(id) => setSelectedAppId(id ?? "")}
                  placeholder={
                    selectableApps.length > 0
                      ? "Select a connector"
                      : "Connect a git provider"
                  }
                  suggested={(app) =>
                    matchesUrlPattern(app.urlPattern, repoUrl.href)
                  }
                  appSubtitle={(app) =>
                    matchesUrlPattern(app.urlPattern, repoUrl.href)
                      ? undefined
                      : `Doesn’t allow ${repoUrl.hostname} yet`
                  }
                  addNew={suggestions.map((suggestion) => ({
                    label: suggestion.label,
                    subtitle: suggestion.subtitle,
                    template: {
                      name: suggestion.label,
                      identifier: suggestion.identifier,
                    },
                    onClick: () =>
                      createConnector(
                        suggestion.identifier,
                        suggestion.overrides
                      ),
                  }))}
                />
              </Field>
            )}
            {repoUrl && selectedApp && (
              <Field>
                {needsWiden &&
                  (canWiden ? (
                    <Text variant="text-small" color="var(--text-secondary)">
                      Connecting adds {repoUrl.hostname} to the allowed URLs of
                      “{selectedApp.name}”.
                    </Text>
                  ) : (
                    <Text variant="text-small" color="var(--text-secondary)">
                      This connector doesn’t allow {repoUrl.hostname}, and you
                      don’t have permission to change its allowed URLs.
                    </Text>
                  ))}
                {configureError && (
                  <div role="alert">
                    <Text variant="text-small" color="var(--text-danger)">
                      Couldn’t connect this repository. If its URL is outside
                      the connector’s allowed URLs, open the{" "}
                      <Link
                        to={makeUrl(routes.modal.connector, {
                          id: selectedApp.id,
                        })}
                      >
                        connector
                      </Link>
                      , re-enter its secrets, and allow this URL there.
                    </Text>
                  </div>
                )}
                <div>
                  <Button
                    variant="primary"
                    onClick={handleConfigure}
                    disabled={
                      !availabilityReady ||
                      configuring ||
                      (needsWiden && !canWiden)
                    }
                  >
                    Connect
                  </Button>
                </div>
              </Field>
            )}
          </>
        )}
      </Card>

      <ConfirmModal
        open={confirmDisconnect !== false}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect git sync"
        description="This will remove the git sync configuration for this space. Workflows will not be deleted."
        confirmDisabled={!remoteReady}
        confirmLabel="Disconnect"
        destructive
      />
    </>
  );
}

// 3B configures the GitLab webhook itself, so this only reports the outcome.
function WebhookStatus({
  spaceId,
  connectorId,
  gitSync,
  disabled,
}: {
  spaceId: string;
  connectorId: string;
  disabled: boolean;
  gitSync: {
    remoteUrl: string;
    directory: string;
    webhookReceivedAt: number | null;
    webhookConfiguredAt: number | null;
    webhookError: string | null;
  };
}) {
  const z = useZero();
  const formatRelativeTime = useFormatRelativeTime();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    if (disabled) return;
    setRetrying(true);
    await z
      .mutate(
        sharedMutators.configureGitSync({
          spaceId,
          remoteUrl: gitSync.remoteUrl,
          connectedAppId: connectorId,
          directory: gitSync.directory,
        })
      )
      .client.finally(() => setRetrying(false));
  };

  return (
    <Field>
      <Text variant="text-regular-md">Webhooks</Text>
      {gitSync.webhookReceivedAt != null ? (
        <Text variant="text-small" color="var(--text-secondary)">
          Connected. GitLab delivered a webhook{" "}
          {formatRelativeTime(gitSync.webhookReceivedAt)}.
        </Text>
      ) : gitSync.webhookError != null ? (
        <>
          <Text variant="text-small" color="var(--red-600)">
            Couldn’t set up the webhook automatically. {gitSync.webhookError}
          </Text>
          <div>
            <Button
              variant="secondary"
              onClick={handleRetry}
              disabled={disabled || retrying}
            >
              Try again
            </Button>
          </div>
        </>
      ) : gitSync.webhookConfiguredAt != null ? (
        <Text variant="text-small" color="var(--text-secondary)">
          Webhook configured. Waiting for the first delivery from GitLab — push
          to the repository to check the connection.
        </Text>
      ) : (
        <Text variant="text-small" color="var(--text-secondary)">
          Setting up the webhook so GitLab tells 3B about pushes and merge
          requests…
        </Text>
      )}
    </Field>
  );
}
